import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Modal de confirmation de suppression de facture
 * Avertit l'utilisateur que la suppression est définitive et inclut la suppression du produit Stripe
 */
const DeleteInvoiceConfirmModal = ({ isOpen, onClose, invoice, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  if (!isOpen || !invoice) return null;

  // ⛔ Bloquer l'affichage si facture payée
  const isFacturePayee = invoice.statut === 'payée';

  // Raisons prédéfinies
  const predefinedReasons = [
    'Facture de test',
    'Doublon',
    'Erreur de saisie',
    'Demande du client',
    'Données incorrectes',
    'Autre (à préciser)'
  ];

  const handleConfirm = async () => {
    // Bloquer si facture payée
    if (isFacturePayee) {
      setError('Impossible de supprimer une facture payée. Cette action est bloquée pour des raisons comptables et légales.');
      return;
    }

    if (confirmText.toUpperCase() !== 'SUPPRIMER') {
      setError('Veuillez taper "SUPPRIMER" pour confirmer');
      return;
    }

    if (!reason || !reason.trim()) {
      setError('Veuillez indiquer la raison de la suppression');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🗑️ Suppression de la facture:', invoice.id);

      // Appeler la fonction de confirmation avec la raison
      await onConfirm(invoice, reason.trim());

      console.log('✅ Facture supprimée avec succès');
      onClose();

    } catch (err) {
      console.error('❌ Erreur suppression facture:', err);
      setError(err.message || 'Échec de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const formatEuro = (montant) => {
    if (typeof montant === 'undefined' || montant === null) return '0,00 €';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        {/* En-tête */}
        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 p-6 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mr-4">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900 dark:text-red-100">
                Supprimer la facture
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                Cette action est irréversible
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-red-400 hover:text-red-600 dark:hover:text-red-300"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Avertissement principal */}
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                  Attention : Suppression définitive
                </h3>
                <p className="text-sm text-red-800 dark:text-red-200">
                  Vous êtes sur le point de supprimer définitivement cette facture.
                  Cette action supprimera :
                </p>
                <ul className="mt-2 space-y-1 text-sm text-red-800 dark:text-red-200">
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    La facture de la base de données
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    Le produit Stripe associé
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    L'attribution au client
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Détails de la facture */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Facture à supprimer
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Numéro :</span>
                <span className="font-mono text-gray-900 dark:text-white">{invoice.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Client :</span>
                <span className="text-gray-900 dark:text-white">{invoice.client?.nom || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Période :</span>
                <span className="text-gray-900 dark:text-white">{invoice.periode?.libelle || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Date :</span>
                <span className="text-gray-900 dark:text-white">{formatDate(invoice.dateGeneration)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Montant :</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {formatEuro(invoice.calcul?.montantTotal || 0)}
                </span>
              </div>
              {invoice.stripe?.produitId && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Produit Stripe :</span>
                  <span className="font-mono text-xs text-gray-900 dark:text-white">
                    {invoice.stripe.produitId}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ⛔ Blocage si facture payée */}
          {isFacturePayee && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100 mb-1">
                    ⛔ Suppression bloquée
                  </h3>
                  <p className="text-sm text-red-800 dark:text-red-200">
                    Cette facture a été <strong>payée</strong> et ne peut pas être supprimée pour des raisons
                    comptables et légales. Si vous devez effectuer une correction, veuillez :
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-red-800 dark:text-red-200 list-disc list-inside">
                    <li>Émettre un avoir ou une note de crédit</li>
                    <li>Contacter le support pour assistance</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Raison de suppression */}
          {!isFacturePayee && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Raison de la suppression *
              </label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {predefinedReasons.map((predefinedReason) => (
                  <button
                    key={predefinedReason}
                    type="button"
                    onClick={() => setReason(predefinedReason)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      reason === predefinedReason
                        ? 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-900 dark:text-red-100'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                    disabled={loading}
                  >
                    {predefinedReason}
                  </button>
                ))}
              </div>

              {/* Champ de saisie personnalisé */}
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError(null);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                rows="3"
                placeholder="Précisez la raison de la suppression..."
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Confirmation par saisie */}
          {!isFacturePayee && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pour confirmer, tapez <span className="font-bold text-red-600">SUPPRIMER</span> en majuscules :
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => {
                  setConfirmText(e.target.value);
                  setError(null);
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                placeholder="Tapez SUPPRIMER"
                disabled={loading}
                autoComplete="off"
              />
            </div>
          )}

          {/* Messages d'erreur */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                {error}
              </p>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              {isFacturePayee ? 'Fermer' : 'Annuler'}
            </button>
            {!isFacturePayee && (
              <button
                type="button"
                onClick={handleConfirm}
                className="flex items-center px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || confirmText.toUpperCase() !== 'SUPPRIMER' || !reason.trim()}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteInvoiceConfirmModal;
